import { useCallback, useState } from "react";

import type { AlfredFeedbackPayloadInterface } from "@/interfaces/alfred";
import type { AlfredFeedbackRatingType } from "@/types";

/**
 * Stored feedback entry
 */
interface FeedbackEntry {
    comment?: string;
    rating: AlfredFeedbackRatingType;
    submittedAt: Date;
}

/**
 * useAlfredFeedback - Hook for managing message feedback
 *
 * Features:
 * - Submit positive/negative feedback for messages
 * - Track feedback state per message
 * - Optimistic UI updates
 *
 * Usage:
 * ```tsx
 * const { submitFeedback, getFeedback, isSubmitting } = useAlfredFeedback();
 *
 * // Submit feedback
 * submitFeedback({ message_id: "123", rating: "positive", comment: "Helpful!" });
 *
 * // Get feedback for a message
 * const feedback = getFeedback("123"); // "positive" | "negative" | undefined
 * ```
 */
export const useAlfredFeedback = () => {
    const [feedbackMap, setFeedbackMap] = useState<Map<string, FeedbackEntry>>(new Map());
    const [isSubmitting, setIsSubmitting] = useState(false);

    /**
     * Submit feedback for a message
     */
    const submitFeedback = useCallback(
        async (payload: AlfredFeedbackPayloadInterface) => {
            setIsSubmitting(true);

            try {
                // Optimistically update local state
                setFeedbackMap((prev) => {
                    const next = new Map(prev);
                    next.set(
                        payload.message_id,
                        {
                            comment: payload.comment,
                            rating: payload.rating,
                            submittedAt: new Date(),
                        },
                    );
                    return next;
                });

                /* TODO: Send feedback to backend API
                   await fetch('/api/alfred/feedback', {
                       method: 'POST',
                       body: JSON.stringify(payload),
                   }); */

                // eslint-disable-next-line no-console
                console.log(
                    "[Alfred Feedback]",
                    payload,
                );
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(
                    "[Alfred Feedback] Error:",
                    error,
                );
                // Revert optimistic update on error
                setFeedbackMap((prev) => {
                    const next = new Map(prev);
                    next["delete"](payload.message_id);
                    return next;
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [],
    );

    /**
     * Get the current feedback for a message
     */
    const getFeedback = useCallback(
        (messageId: string): AlfredFeedbackRatingType | undefined => feedbackMap.get(messageId)?.rating,
        [feedbackMap],
    );

    /**
     * Clear all feedback (e.g., when starting a new conversation)
     */
    const clearFeedback = useCallback(
        () => setFeedbackMap(new Map()),
        [],
    );

    return {
        clearFeedback,
        getFeedback,
        isSubmitting,
        submitFeedback,
    };
};

// Legacy exports for backwards compatibility
export type AlfredFeedbackRating = AlfredFeedbackRatingType;
export type FeedbackRating = AlfredFeedbackRatingType;
export const useFeedback = useAlfredFeedback;
